import { useCallback, useEffect, useRef, useState } from 'react';

const readLocalStorage = (key: string): string | null | undefined => {
  try {
    return localStorage.getItem(key);
  } catch {
    return undefined;
  }
};

const readStoredValue = <T>(key: string, fallback: T, { removeIfInvalid }: { removeIfInvalid: boolean }): T => {
  const raw = readLocalStorage(key);
  if (raw === null || raw === undefined) return fallback;
  try {
    return JSON.parse(raw);
  } catch (error) {
    console.error(`Failed to parse saved local state for key "${key}":`, error);
    if (removeIfInvalid) {
      try {
        localStorage.removeItem(key);
      } catch {
        // Storage is blocked; nothing to clean up.
      }
    }
    return fallback;
  }
};

/**
 * Like `useSessionState`, but in `localStorage`, so the value is shared by every tab and survives
 * closing the browser. Tabs already open follow a change made in another tab, and
 * `onChangeFromOtherTab` hears about it.
 */
export function useLocalState<T>(
  key: string,
  initialValue: T,
  onChangeFromOtherTab?: () => void
): [T, (value: T) => void] {
  const initialValueReference = useRef(initialValue);
  const onChangeFromOtherTabReference = useRef(onChangeFromOtherTab);
  onChangeFromOtherTabReference.current = onChangeFromOtherTab;
  const readInitialValue = () => readStoredValue(key, initialValueReference.current, { removeIfInvalid: true });
  const [stored, setStored] = useState(() => ({ key, value: readInitialValue() }));
  if (stored.key !== key) setStored({ key, value: readInitialValue() });

  const setValue = useCallback(
    (value: T) => {
      setStored({ key, value });
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Storage is blocked (some privacy modes); the value lasts until the page reloads.
      }
    },
    [key]
  );

  useEffect(() => {
    // Reads storage rather than the event's value: events can arrive late or be about sessionStorage.
    const followOtherTabs = (event: StorageEvent) => {
      const clearedAllStorage = event.key === null;
      if (event.key !== key && !clearedAllStorage) return;
      setStored({ key, value: readStoredValue(key, initialValueReference.current, { removeIfInvalid: false }) });
      onChangeFromOtherTabReference.current?.();
    };
    globalThis.addEventListener('storage', followOtherTabs);
    return () => globalThis.removeEventListener('storage', followOtherTabs);
  }, [key]);

  return [stored.value, setValue];
}
